import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React, { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { set, lensPath } from 'ramda';
import { compact, useFeatureFlag } from '@moda/portal-stanchions';
import { titleize, formatVertical } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { usePLPParams } from '../../hooks/usePLPParams';
import { ModuleSection } from '../../components/ModuleSection';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { SubcategoryLinks } from '../../components/SubcategoryLinks/SubcategoryLinks';
import {
  DesignerPageQuery,
  DesignerPageQueryVariables
} from '../../generated/types/DesignerPageQuery';
import { findSectionModules } from '../../lib/findSectionModules';
import { NotFoundPage } from '../NotFoundPage';
import { LazyLoad, EnableLazyLoad } from '../../components/LazyLoad';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { DesignerPageFollowButton } from '../../components/DesignerPageFollowButton';
import { getRuleContextFromSlug, FacadePageType } from '../../lib/getRuleContextFromSlug';

export const DESIGNER_PAGE_QUERY: TypedDocumentNode<
  DesignerPageQuery,
  DesignerPageQueryVariables
> = gql`
  query DesignerPageQuery(
    $after: String!
    $countryCode: String
    $availability: [AvailabilityInput]
    $filterColors: [String]
    $materials: [String]
    $designers: [String]
    $sizes: [String]
    $categoryPath: String
    $attributes: [String]
    $onSale: Boolean
    $noSale: Boolean
    $priceRange: PriceRange
    $saleTags: [String]
    $slug: String!
    $versionId: Int
    $scoring: String
    $ruleContexts: [String]
  ) {
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      meta_description
    }

    designer(slug: $slug) {
      id
      name
      slug
    }

    designerCollection: designer_collection(
      slug: $slug
      input: {
        country_code: $countryCode
        availability: $availability
        filter_colors: $filterColors
        materials: $materials
        designers: $designers
        sizes: $sizes
        category_path: $categoryPath
        attributes: $attributes
        on_sale: $onSale
        no_sale: $noSale
        price_range: $priceRange
        sale_tags: $saleTags
        version_id: $versionId
        scoring: $scoring
        rule_contexts: $ruleContexts
      }
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PUMO_MODULE_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

export const DesignerPage: React.FC = () => {
  const showSaleInPLPs = useFeatureFlag('show-sale-in-plps', [
    { default: false },
    { if: true, then: true }
  ]);
  const useRulesFeatureFlag = useFeatureFlag(`use-facade-rules`, [
    { default: false },
    { if: true, then: true }
  ]);
  const {
    params: { vertical, category, subcategory, designerSlug },
    queryVariables
  } = usePLPParams();
  const ruleAbTest = useFeatureFlag(`${queryVariables.slug}_test`, [
    { default: false },
    { if: true, then: true }
  ]);
  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(DESIGNER_PAGE_QUERY, {
    variables: {
      ...queryVariables,
      noSale: !showSaleInPLPs,
      ...(useRulesFeatureFlag
        ? {
            ruleContexts: [
              getRuleContextFromSlug(FacadePageType.Designer, queryVariables.slug, ruleAbTest)
            ]
          }
        : {})
    }
  });

  const data = currentData ?? previousData;

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const firstAttribute = queryVariables.attributes?.[0];
  const designerName = data?.designerCollection?.pageName || '';
  const categoryTree = data?.designerCollection?.categoryTree;
  const categoryText = firstAttribute || subcategory || category;
  const dataWithoutDesigners = data && set(lensPath(['designerCollection', 'designers']), [], data);

  const title = compact([
    designerName,
    categoryText || 'Fashion Collections',
    vertical && `for ${titleize(vertical)}`
  ]).join(' ');

  const headerText = compact([
    designerName,
    categoryText && titleize(categoryText),
    vertical && `for ${titleize(vertical)}`
  ]).join(' ');

  const description = compact([
    'Shop new',
    vertical && formatVertical(vertical),
    designerName,
    categoryText || 'collection',
    'on Moda Operandi.'
  ]).join(' ');

  if (vertical === 'men') {
    return <Redirect to={hrefFor.HomePage()} />;
  }

  // if there are no results for the vertical, redirect to the first available vertical
  if (
    categoryTree?.children?.length &&
    !categoryTree.children.find(child => child?.slug === vertical)
  )
    return (
      <Redirect
        to={hrefFor.DesignerPage({
          designerSlug: designerSlug as string,
          vertical: categoryTree?.children[0]?.slug as string
        })}
      />
    );

  if (data && !data.designerCollection) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}

      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <ProductListing
        title={title}
        description={description}
        headerText={headerText}
        queryResult={dataWithoutDesigners?.designerCollection}
        error={error}
        loading={loading}
        belowHeader={<DesignerPageFollowButton />}
      >
        <SubcategoryLinks
          designerName={designerName}
          categoryTree={dataWithoutDesigners?.designerCollection?.categoryTree || null}
        />
      </ProductListing>

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};
